'use client';

import React, { useCallback, useEffect } from 'react';
import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';

import useInfiniteScroll from 'src/hooks/useInfiniteScroll';

import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';

import Message from 'src/components/job-slide-out/components/nav-table/message/message';

import { updateMessage } from 'src/redux/jobs/slice';
import { fetchMessages } from 'src/redux/jobs/api';
import {
  JobDetailsChatBottomIcon, PmNoMessageIcon,
} from 'src/assets/images/job-details-slide-out/job-details-slide-out';
import type { MessageCategory } from 'src/types';

interface MessageContent {
  message_id: number;
  [key: string]: unknown;
}

const ChatSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const type = useAppSelector((state) => state?.jobDetails?.slideOut?.type);
  const messages = useAppSelector(
    (state) => state?.jobDetails?.messages,
  ) as Record<string, unknown>;

  const activeThreadId = useAppSelector(
    (state) => (state?.jobDetails?.messages as Record<string, unknown>)?.activeThreadId,
  ) as number | undefined;
  const jobId = useAppSelector(
    (state) => state?.jobDetails?.data?.root_node_id,
  ) as string | undefined;
  const serviceRequestId = useAppSelector(
    (state) => state?.jobDetails?.data
      ?.service_request_id,
  );
  const hasInitialMessageApiTriggered = useAppSelector(
    (state) => (state?.jobDetails?.messages as Record<string, unknown>)?.hasInitialMessageApiTriggered,
  ) as boolean | undefined;
  const isFetchingMessages = useAppSelector((state) => state?.jobDetails?.isFetchingMessages);
  const isFetchingMessageThreads = useAppSelector(
    (state) => state?.jobDetails?.isFetchingMessageThreads,
  );
  useEffect(() => {
    const messagesData = messages?.data as Record<string, MessageCategory> | undefined;
    if (isEmpty(messagesData) && !hasInitialMessageApiTriggered && activeThreadId) {
      dispatch(updateMessage({
        hasInitialMessageApiTriggered: true,
      }));
      dispatch(fetchMessages(
        serviceRequestId,
        activeThreadId != null ? String(activeThreadId) : undefined,
      )(type === 'jobDetails' ? { job_id: jobId } : {})).unwrap().then(() => {
      }).catch((e: Record<string, unknown>) => {
        toast(
          (e?.message as string)
          || (e?.errors as Record<string, string[]>)
            ?.general?.[0]
          || 'Something went wrong',
          {
            type: 'error',
          },
        );
      });
    }
  }, [
    activeThreadId,
    dispatch,
    hasInitialMessageApiTriggered,
    jobId,
    messages?.data,
    serviceRequestId,
    type]);

  const meta = messages?.meta as Record<string, unknown> | undefined;
  const links = messages?.links as Record<string, unknown> | undefined;

  const fetchNextPageContent = useCallback(() => {
    const messagesData = messages?.data as Record<string, MessageCategory> | undefined;
    if (!isEmpty(messagesData) && links?.next) {
      dispatch(fetchMessages(
        serviceRequestId,
        activeThreadId != null ? String(activeThreadId) : undefined,
      )(type === 'jobDetails'
        ? {
          job_id: jobId,
          page: (meta?.current_page as number) + 1,
        }
        : {
          page: (meta?.current_page as number) + 1,
        })).unwrap().then(() => { }).catch(
        (e: Record<string, unknown>) => {
          toast(
            (e?.message as string)
            || (e?.errors as Record<string, string[]>)
              ?.general?.[0]
            || 'Something went wrong',
            { type: 'error' },
          );
        },
      );
    }
  }, [
    activeThreadId,
    dispatch,
    jobId,
    messages?.data,
    links?.next,
    serviceRequestId,
    meta?.current_page,
    type,
  ]);

  const targetRef = useInfiniteScroll(
    true,
    fetchNextPageContent,
    'message-wrapper',
    '50px',
    0.2,
  );

  const messagesData = messages?.data as Record<string, MessageCategory> | undefined;

  // "No messages yet" is only true once an initial fetch has actually
  // completed. While the thread list is still loading, or a thread is selected
  // but its first message fetch hasn't finished, show the loader instead —
  // otherwise the empty state flashes for the whole thread-list fetch.
  const isAwaitingInitialLoad = isFetchingMessages
    || isFetchingMessageThreads
    || (!!activeThreadId && !hasInitialMessageApiTriggered);

  if (isEmpty(messagesData)) {
    if (isAwaitingInitialLoad) {
      return (
        <div className="no-message-data">
          <span className="spinner-border spinner-border-sm mr-2 d-block" />
        </div>
      );
    }

    return (
      <div className="no-message-data">
        <PmNoMessageIcon />
        <span>No messages yet</span>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className={`${type === 'jobDetails' ? 'has-bottom-banner' : ''} message-inner`}>
        {isFetchingMessages
          ? (
            <div className="spinner-outer h-100">
              <span className="spinner-border spinner-border-sm mr-2 d-block" />
            </div>
          )
          : ''}
        {!isEmpty(messagesData) && messagesData && Object.keys(messagesData)?.map((item, index) => {
          const lastIndex = Object.keys(messagesData).length - 1;
          const messageDate = messagesData?.[item]?.value;

          const messagesFilteredByDate = messagesData?.[item]?.content;

          return (
            <React.Fragment key={item}>
              {messagesFilteredByDate?.map((content) => (
                <Message
                  messageContent={content as MessageContent}
                  dateGroup={item}
                  key={content?.message_id}
                />
              ))}
              {messageDate && (
                <div
                  className="date-divider"
                  {...(index === lastIndex ? { ref: targetRef } : {})}
                >
                  <span>
                    {messageDate}
                  </span>
                </div>
              )}
              {(index === lastIndex && isFetchingMessages) ? (
                <div className="spinner-outer scroll-top-spinner">
                  <span className="spinner-border spinner-border-sm mr-2 d-block" />
                </div>
              ) : ''}
            </React.Fragment>
          );
        })}
      </div>
      {type === 'jobDetails'
        ? (
          <div className="mr-info">
            {!isEmpty(messagesData)
              ? (
                <React.Fragment>
                  <JobDetailsChatBottomIcon />
                  <p>All messages from maintenance request included</p>
                </React.Fragment>
              )
              : ''}
          </div>
        ) : ''}
    </React.Fragment>
  );
};

export default ChatSection;
